import { useState, useRef } from "react";
import { dbApplyWo, dbLoadStateLite, dbInsertChallenge, dbUpdateChallenge, dbDeleteChallenge, dbInsertResult, dbInsertNotifications, dbMarkNotifsRead, dbInsertPositionHistory, dbSavePlayers } from "../lib/supabase";
import { CONFIRM_DEADLINE_DAYS, GENDER } from "../constants";
import { isChallengerBlocked, getBlockStatus, fmtDate } from "../lib/helpers";
import { nowBR } from "./useAuth";

// ─── SUPABASE CONFIG (reutiliza as constantes já definidas em supabase.js) ────
const SUPABASE_URL = "https://mcyiufzzwcznaspfdfoy.supabase.co";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1jeWl1Znp6d2N6bmFzcGZkZm95Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI2NDA5NDAsImV4cCI6MjA4ODIxNjk0MH0.ITRG4U7TqPH3UYh06K1UdRi7LUwqVH5Gab6Lq7WmjKM";

/**
 * Helper: constrói entradas de histórico de posição.
 * - Jogadores em `alwaysInclude` são sempre gravados (mesmo sem mudança de posição).
 * - Demais jogadores só são gravados se a posição mudou.
 */
export function buildPositionHistoryEntries(players, prevPositions, now, reasonMap, alwaysInclude = []) {
  const entries = [];
  players.forEach(p => {
    const prev = prevPositions[p.id];
    const changed = prev !== undefined && prev !== p.position;
    const forced = alwaysInclude.includes(p.id);
    if (!changed && !forced) return;
    const rm = reasonMap[p.id];
    entries.push({
      playerId: p.id,
      playerName: p.name,
      position: p.position,
      reason: rm ? rm.reason : "passive",
      detail: rm ? rm.detail : `Reposicionado (${prev > p.position ? "subiu" : "caiu"} ${Math.abs(prev - p.position)} posição(ões))`,
      date: now,
    });
  });
  return entries;
}

export function useChallenges({ state, save, currentUser, flash, gender }) {
  // ── Modais de desafio ───────────────────────────────────────────────────────
  const [pyramidModal, setPyramidModal]           = useState(null);
  const [modalDates, setModalDates]               = useState(["", "", ""]);
  const [confirmModal, setConfirmModal]           = useState(null);
  const [pickedDate, setPickedDate]               = useState("");
  const [statsModal, setStatsModal]               = useState(null);
  const [counterModal, setCounterModal]           = useState(null);
  const [counterDates, setCounterDates]           = useState(["", "", ""]);
  const [refuseModal, setRefuseModal]             = useState(null);
  const [mediationModal, setMediationModal]       = useState(null);
  const [mediationSchedule, setMediationSchedule] = useState(null);
  const [mediationDate, setMediationDate]         = useState("");
  const [pyramidTooltip, setPyramidTooltip]       = useState(null);

  // ── Proposta de desafio ─────────────────────────────────────────────────────
  const [challengeTarget, setChallengeTarget] = useState("");
  const [challengeDates, setChallengeDates]   = useState(["", "", ""]);

  // ── Ranking / busca ─────────────────────────────────────────────────────────
  const [rankSearch, setRankSearch] = useState("");

  // ── Agenda ──────────────────────────────────────────────────────────────────
  const [agendaView, setAgendaView]     = useState("list");
  const [calendarDate, setCalendarDate] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  // Trava contra duplo-clique / reentrância ao propor desafio
  const submittingChallengeRef = useRef(false);

  // ── Ações ───────────────────────────────────────────────────────────────────
  async function submitChallenge(targetId, dates) {
    if (submittingChallengeRef.current) return; // evita envio duplicado (duplo clique / reentrância)
    submittingChallengeRef.current = true;
    try {
      const s = JSON.parse(JSON.stringify(state));
      const meP = s.players.find(p => p.id === currentUser);
      const target = s.players.find(p => p.id === targetId);
      if (!meP || !target) return;
      const activeAsChallenger = s.challenges.filter(c => c.challengerId === meP.id).length;
      if (activeAsChallenger >= 1) { flash("Você já possui um desafio ativo como desafiante.", "error"); return; }
      if (isChallengerBlocked(meP)) {
        const dt = meP.blockedAsChallenger ? new Date(meP.blockedAsChallenger).toLocaleDateString("pt-BR") : null;
        flash("Você está indisponível para propor desafios" + (dt ? " até " + dt : "") + ".", "error"); return;
      }
      const targetBlockStatus = getBlockStatus(target);
      if (targetBlockStatus === "inativo") {
        const dt = target.inactiveUntil ? new Date(target.inactiveUntil).toLocaleDateString("pt-BR") : null;
        flash(target.name + " está inativo e não pode ser desafiado" + (dt ? " até " + dt : "") + ".", "error"); return;
      }
      if (targetBlockStatus === "bloqueado") {
        const dt = target.blockedAsChallenged ? new Date(target.blockedAsChallenged).toLocaleDateString("pt-BR") : null;
        flash(target.name + " está indisponível para receber desafios" + (dt ? " até " + dt : "") + ".", "error"); return;
      }
      const activeAsChallengee = s.challenges.filter(c => c.challengedId === target.id).length;
      if (activeAsChallengee >= 1) { flash(target.name + " já possui um desafio ativo como desafiado.", "error"); return; }
      const newChallenge = {
        challengerId: meP.id, challengedId: target.id,
        challengerName: meP.name, challengedName: target.name,
        dates: [...dates], proposedAt: nowBR(),
        status: "aguardando_confirmacao", confirmedDate: null,
        lastProposedBy: "challenger",
      };
      try {
        const newId = await dbInsertChallenge(newChallenge, gender);
        newChallenge.id = newId;
        s.challenges.push(newChallenge);
        const notif = { playerId: target.id, text: `${meP.name} te desafiou! Você tem ${CONFIRM_DEADLINE_DAYS} dias para confirmar uma data.`, date: nowBR(), read: false };
        await dbInsertNotifications([notif], gender);
        s.notifications.unshift({ ...notif, id: Date.now() });
        save(s); flash("Desafio proposto para " + target.name + "!");
      } catch (e) { console.error(e); flash("Erro ao propor desafio.", "error"); }
    } finally {
      submittingChallengeRef.current = false;
    }
  }

  function proposeChallenge() {
    if (!challengeTarget || challengeDates.filter(d => d.trim()).length < 3) { flash("Selecione o adversário e preencha as 3 datas.", "error"); return; }
    const hasWeekend = challengeDates.some(d => { if (!d) return false; const dow = new Date(d.split("T")[0] + "T12:00:00").getDay(); return dow === 0 || dow === 6; });
    if (!hasWeekend) { flash("Pelo menos uma das datas deve ser no fim de semana (sábado ou domingo).", "error"); return; }
    const hasWeekday = challengeDates.some(d => { if (!d) return false; const dow = new Date(d.split("T")[0] + "T12:00:00").getDay(); return dow >= 1 && dow <= 5; });
    if (!hasWeekday) { flash("Pelo menos uma das datas deve ser em dia de semana (segunda a sexta).", "error"); return; }
    submitChallenge(parseInt(challengeTarget), challengeDates);
    setChallengeTarget(""); setChallengeDates(["", "", ""]);
  }

  function proposeFromPyramid() {
    if (!pyramidModal || modalDates.filter(d => d.trim()).length < 3) { flash("Preencha as 3 opções de data.", "error"); return; }
    const hasWeekendP = modalDates.some(d => { if (!d) return false; const dow = new Date(d.split("T")[0] + "T12:00:00").getDay(); return dow === 0 || dow === 6; });
    if (!hasWeekendP) { flash("Pelo menos uma das datas deve ser no fim de semana (sábado ou domingo).", "error"); return; }
    const hasWeekdayP = modalDates.some(d => { if (!d) return false; const dow = new Date(d.split("T")[0] + "T12:00:00").getDay(); return dow >= 1 && dow <= 5; });
    if (!hasWeekdayP) { flash("Pelo menos uma das datas deve ser em dia de semana (segunda a sexta).", "error"); return; }
    submitChallenge(pyramidModal.id, modalDates);
    setPyramidModal(null); setModalDates(["", "", ""]);
  }

  async function confirmChallengeDate() {
    if (!confirmModal || !pickedDate) { flash("Selecione uma data.", "error"); return; }
    const s = JSON.parse(JSON.stringify(state));
    const ch = s.challenges.find(c => c.id === confirmModal.id);
    if (!ch) return;
    ch.confirmedDate = pickedDate; ch.status = "agendado";
    const chr = s.players.find(p => p.id === ch.challengerId);
    try {
      await dbUpdateChallenge(ch.id, { status: "agendado", confirmedDate: pickedDate, lastProposedBy: "challenger" }, gender);
      const notif = { playerId: chr.id, text: `${s.players.find(p => p.id === ch.challengedId)?.name || ""} confirmou o desafio para ${fmtDate(pickedDate)}.`, date: nowBR(), read: false };
      await dbInsertNotifications([notif], gender);
      s.notifications.unshift({ ...notif, id: Date.now() });
      save(s); setConfirmModal(null); setPickedDate(""); flash("Data confirmada: " + fmtDate(pickedDate));
    } catch (e) { console.error(e); flash("Erro ao confirmar data.", "error"); }
  }

  async function refuseChallenge(challengeId) {
    const ch = state.challenges.find(c => c.id === challengeId);
    if (!ch) return;
    try {
      // Penalidade aplicada no banco, em uma única transação (apply_wo_penalty)
      const r = await dbApplyWo({ gender, challengeId, type: "wo-challenged", origin: "recusa" });
      const fresh = await dbLoadStateLite(gender, state);
      save(fresh); setRefuseModal(null);
      if (r && r.applied) flash("Desafio recusado. W.O. registrado.");
      else flash("Este desafio já foi processado.", "info");
    } catch (e) { console.error(e); flash("Erro ao recusar desafio.", "error"); }
  }

  async function requestMediation(challengeId) {
    const s = JSON.parse(JSON.stringify(state));
    const ch = s.challenges.find(c => c.id === challengeId);
    if (!ch) return;
    const now = nowBR();
    try {
      await dbUpdateChallenge(challengeId, { status: "em_mediacao" }, gender);
      ch.status = "em_mediacao";
      const newNotifs = [
        { playerId: ch.challengedId, text: "Desafio encaminhado para mediação do admin.", date: now, read: false },
        { playerId: ch.challengerId, text: "Desafio encaminhado para mediação do admin.", date: now, read: false },
      ];
      await dbInsertNotifications(newNotifs, gender);
      newNotifs.forEach(n => s.notifications.unshift({ ...n, id: Date.now() + Math.random() }));
      save(s); setMediationModal(null); flash("Desafio encaminhado para mediação.");
    } catch (e) { console.error(e); flash("Erro ao encaminhar mediação.", "error"); }
  }

  async function counterProposeDates() {
    const me = state?.players.find(p => p.id === currentUser);
    if (!counterModal || counterDates.filter(d => d.trim()).length < 3) { flash("Preencha as 3 datas.", "error"); return; }
    const hasWeekendC = counterDates.some(d => { if (!d) return false; const dow = new Date(d.split("T")[0] + "T12:00:00").getDay(); return dow === 0 || dow === 6; });
    if (!hasWeekendC) { flash("Pelo menos uma das datas deve ser no fim de semana (sábado ou domingo).", "error"); return; }
    const hasWeekdayC = counterDates.some(d => { if (!d) return false; const dow = new Date(d.split("T")[0] + "T12:00:00").getDay(); return dow >= 1 && dow <= 5; });
    if (!hasWeekdayC) { flash("Pelo menos uma das datas deve ser em dia de semana (segunda a sexta).", "error"); return; }
    const s = JSON.parse(JSON.stringify(state));
    const ch = s.challenges.find(c => c.id === counterModal.id);
    if (!ch) return;
    ch.dates = [...counterDates]; ch.status = "aguardando_confirmacao"; ch.confirmedDate = null;
    ch.proposedAt = nowBR(); ch.lastProposedBy = "challenged";
    const chr = s.players.find(p => p.id === ch.challengerId);
    try {
      await dbUpdateChallenge(ch.id, { proposed_dates: counterDates, status: "aguardando_confirmacao", confirmedDate: null, proposedAt: ch.proposedAt, lastProposedBy: "challenged" }, gender);
      const notif = { playerId: chr.id, text: `${me?.name || ""} propôs novas datas para o desafio. Escolha uma para confirmar.`, date: nowBR(), read: false };
      await dbInsertNotifications([notif], gender);
      s.notifications.unshift({ ...notif, id: Date.now() });
      save(s); setCounterModal(null); setCounterDates(["", "", ""]); flash("Novas datas propostas!");
    } catch (e) { console.error(e); flash("Erro ao propor novas datas.", "error"); }
  }

  async function cancelChallengeByChallenger(challengeId) {
    const s = JSON.parse(JSON.stringify(state));
    const ch = s.challenges.find(c => c.id === challengeId);
    if (!ch) return;
    // Só permite cancelar se o desafiado ainda não respondeu
    if (ch.status !== "aguardando_confirmacao" || ch.lastProposedBy !== "challenger") {
      flash("Não é possível cancelar este desafio.", "error"); return;
    }
    const now = nowBR();

    // Notificação in-app para o desafiado
    const notif = {
      playerId: ch.challengedId,
      text: `${ch.challengerName} cancelou o desafio proposto a você.`,
      date: now,
      read: false,
    };

    try {
      // 1. Envia email via Edge Function ANTES de deletar o registro
      //    (após dbDeleteChallenge o webhook não teria mais acesso ao registro)
      fetch(
        `${SUPABASE_URL}/functions/v1/notify-challenge`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            type: "CANCEL",
            record: {
              id: ch.id,
              status: "cancelado_pelo_desafiante",
              challenger_id: ch.challengerId,
              challenged_id: ch.challengedId,
              challenger_name: ch.challengerName,
              challenged_name: ch.challengedName,
            },
            table: GENDER.tables[gender]?.challenges ?? GENDER.tables.M.challenges,
          }),
        }
      ).catch(e => console.warn("Email cancelamento error:", e)); // fire-and-forget: não bloqueia o fluxo

      // 2. Deleta o desafio e salva notificação in-app
      await dbDeleteChallenge(challengeId, gender);
      await dbInsertNotifications([notif], gender);
    } catch (e) {
      console.error(e);
      flash("Erro ao cancelar desafio.", "error");
      return;
    }

    s.notifications.unshift({ ...notif, id: Date.now() });
    s.challenges = s.challenges.filter(c => c.id !== challengeId);
    save(s);
    flash("Desafio cancelado.");
  }

  async function markAllRead(uid) {
    const s = JSON.parse(JSON.stringify(state));
    s.notifications.forEach(n => { if (n.playerId === uid) n.read = true; });
    save(s);
    await dbMarkNotifsRead(uid, gender);
  }

  return {
    // modais
    pyramidModal, setPyramidModal,
    modalDates, setModalDates,
    confirmModal, setConfirmModal,
    pickedDate, setPickedDate,
    statsModal, setStatsModal,
    counterModal, setCounterModal,
    counterDates, setCounterDates,
    refuseModal, setRefuseModal,
    mediationModal, setMediationModal,
    mediationSchedule, setMediationSchedule,
    mediationDate, setMediationDate,
    pyramidTooltip, setPyramidTooltip,

    // proposta
    challengeTarget, setChallengeTarget,
    challengeDates, setChallengeDates,

    // ranking
    rankSearch, setRankSearch,

    // agenda
    agendaView, setAgendaView,
    calendarDate, setCalendarDate,

    // ações
    submitChallenge, proposeChallenge, proposeFromPyramid,
    confirmChallengeDate, refuseChallenge, requestMediation,
    counterProposeDates, cancelChallengeByChallenger, markAllRead,
  };
}
